import CheckIcon from "@mui/icons-material/Check";
import RemoveIcon from "@mui/icons-material/Remove";
import Box from "@mui/material/Box";
import MuiCheckbox, { CheckboxProps as MuiCheckboxProps } from "@mui/material/Checkbox";
import { SwitchBaseProps } from "@mui/material/internal/SwitchBase";
import { styled } from "@mui/material/styles";
import { CSSProperties } from "@mui/material/styles/createTypography";
import clsx from "clsx";
import FormControlLabel from "designSystem/components/form/FormControlLabel";
import { black, blue, grey, red, white } from "designSystem/styles/color";
import { disabledCursorStyling } from "designSystem/styles/style-functions";
import { ARIA_LABEL } from "designSystem/wai-aria";
import React, { FunctionComponent, useCallback, useState } from "react";
import { FormControlLabelProps } from "../form/FormControlLabel";

const PREFIX = "Checkbox";

const classes = {
	root: `${PREFIX}root`,
	check: `${PREFIX}check`,
	clickableArea: `${PREFIX}clickableArea`,
	empty: `${PREFIX}empty`,
	invalidRoot: `${PREFIX}invalidRoot`,
	disabledOuterBox: `${PREFIX}disabledOuterBox`,
	outerBox: `${PREFIX}outerBox`,
};

const styles = {
	[`& .${classes.root}`]: {
		".csi-outerBox:hover &,.csi-outer-label:hover &": {
			borderColor: grey[900],
		},
		".csi-outerBox:active &": {
			borderColor: blue[600],
		},
		"&.Mui-checked": {
			".csi-outerBox:hover &,.csi-outer-label:hover &": {
				backgroundColor: blue[700],
			},
		},
	},

	[`& .${classes.check}`]: {
		color: white[50],
		fontSize: "14px",
		height: 14,
		width: 14,

		verticalAlign: "initial",
	},

	[`& .${classes.clickableArea}`]: {
		position: "absolute",

		height: 24,
		width: 24,

		zIndex: 2,
	},

	[`& .${classes.empty}`]: {
		fontSize: "14px",
	},

	[`& .${classes.invalidRoot}`]: {
		borderColor: red[600],
		".csi-outerBox:hover &,.csi-outer-label:hover &": {
			borderColor: red[700],
		},
		"&.Mui-checked.Mui-checked": {
			backgroundColor: red[600],
			borderColor: "transparent",

			".csi-outerBox:hover &,.csi-outer-label:hover &": {
				backgroundColor: red[700],
			},
		},
	},

	[`&.${classes.disabledOuterBox}`]: {
		"&.csi-outerBox": {
			cursor: "not-allowed",
		},
	},

	[`&.${classes.outerBox}`]: {
		cursor: "pointer",
		display: "inline-block",
		position: "relative",
		textAlign: "left",

		height: 24,
		width: 24,
	},
};

const StyledFormControlLabel = styled(FormControlLabel)(({
	theme,
}) => (styles));

const StyledBox = styled(Box)(({
	theme,
}) => (styles));

export interface CheckboxProps extends Omit<MuiCheckboxProps, "checkedIcon" | "icon" | "indeterminateIcon"> {
	/* props to be passed to input element */
	inputProps?: SwitchBaseProps["inputProps"];

	/* True if the checkbox value is invalid */
	invalid?: boolean;

	/* If this is included, the checkbox will be a formControllabel and pass these props to it */
	labelProps?: Omit<FormControlLabelProps, "control">;
}

const Checkbox: FunctionComponent<CheckboxProps> = (props) => {
	const { className, inputProps, invalid, labelProps, ...otherProps } = props;
	const [focusChild, setFocusChild] = useState(undefined);
	const {
		check,
		clickableArea,
		disabledOuterBox,
		empty,
		invalidRoot,
		outerBox,
		root,
	} = classes;

	const inputRef = useCallback((node: HTMLElement) => {
		setFocusChild(node);
	}, []);

	const onClick = (event): void => {
		if (props.onChange) {
			event.stopPropagation();
			event.preventDefault();
			if (!props.disabled) {
				props.onChange(event, !props.checked);
				focusChild?.focus();
			}
		}
	};

	const labelCursor = disabledCursorStyling(props.disabled);
	const labelColor = props.disabled ? grey[800] : black[100];
	const indeterminateIcon = props.checked ? <RemoveIcon className={check} /> : <Box />;

	const innerComponent = (
		<StyledBox className={clsx("csi-outerBox", outerBox, !labelProps && className, props.disabled && disabledOuterBox)}>
			<Box
				className={clickableArea}
				onClick={onClick}
			/>
			<MuiCheckbox
				{...otherProps}
				checkedIcon={<CheckIcon className={check} />}
				className={`${props.invalid ? invalidRoot : ""} ${root}`}
				data-testid="Checkbox"
				icon={<Box className={empty} />}
				indeterminateIcon={indeterminateIcon}
				inputProps={{
					[ARIA_LABEL]: `${labelProps?.label}`,
					...inputProps,
				}}
				inputRef={inputRef}
				size="small"
			/>
		</StyledBox>
	);

	if (labelProps) {
		return (
			<StyledFormControlLabel
				className={clsx(className, "csi-outer-label")}
				control={innerComponent}
				labelPlacement="end"
				labelStyle={{
					cursor: labelCursor,
					alignItems: "center",
					color: labelColor,
					display: "inline-flex",
					...(labelProps.sx || {}),
				} as CSSProperties}
				{...labelProps}
			/>
		);
	} else {
		return innerComponent;
	}
};

export default Checkbox;